// src/hooks/useCredits.js - Real implementation with Stripe
// Reads credits directly from Firestore (instant) instead of going
// through the Cloud Run backend (which may be cold-starting).
import { useState, useEffect, useCallback, useMemo } from "react";
import { doc, getDoc } from "firebase/firestore";
import { db, canUseFirestore } from "../firebase";
import { apiService } from "../apiService";

// Default subscription plans — used as the synchronous initial state for
// pricing UIs (so plan cards paint on first render instead of popping in
// after the async fetch resolves) and as the fallback when the fetch fails.
export const DEFAULT_SUBSCRIPTION_PLANS = {
  student: {
    id: "student",
    name: "Student",
    credits_per_month: 50,
    price_cents: 1499,
    yearly_price_cents: 9990,
    description: "50 videos/month",
  },
  // Plan id stays "premium" — only the display name is "Super". Mirrors settings.py.
  premium: {
    id: "premium",
    name: "Super",
    credits_per_month: 120,
    price_cents: 1999,
    yearly_price_cents: 19990,
    description: "120 videos/month",
  },
};

export const useCredits = (userId, userData) => {
  const [loading, setLoading] = useState(true);
  const [error] = useState(null);

  const credits = useMemo(() => {
    if (!userData) {
      return {
        available: 0,
        total_used: 0,
        subscription_plan: null,
        monthly_cap: 0,
        used_this_month: 0,
      };
    }

    const limit = userData.video_limit_this_month || 0;
    const used = userData.video_spend_this_month || 0;

    return {
      available: Math.max(limit - used, 0),
      total_used: userData.videos_generated || 0,
      subscription_plan: userData.video_subscription_tier || "free",
      monthly_cap: limit,
      used_this_month: used,
    };
  }, [userData]);

  const dailyUsage = useMemo(() => {
    return {
      used: credits.used_this_month,
      remaining: credits.available,
      daily_limit: credits.monthly_cap,
    };
  }, [credits]);

  const isSubscriber =
    credits.subscription_plan && credits.subscription_plan !== "free";

  useEffect(() => {
    if (userData !== undefined) {
      setLoading(false);
    }
  }, [userData]);

  const hasCredits = (needed = 1) => {
    return credits.available >= needed;
  };

  const purchaseCredits = async (packageId) => {
    if (!userId) throw new Error("You must be signed in to purchase credits");

    try {
      const data = await apiService.createPaymentSession({
        package_id: packageId,
        user_id: userId,
        success_url: `${window.location.origin}/credits/success`,
        cancel_url: `${window.location.origin}/credits`,
      });

      if (data.url) {
        window.location.href = data.url;
        return new Promise(() => {}); // Never resolves — page is navigating away
      } else {
        throw new Error("No checkout URL received from server");
      }
    } catch (error) {
      console.error("Error purchasing credits:", error);
      throw error;
    }
  };

  const getCreditPackages = useCallback(async () => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);

      const data = await apiService.getCreditPackages({
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
      return data.packages;
    } catch (error) {
      console.error("Error fetching credit packages:", error.message);
    }

    // Fallback packages if API fails
    return {
      starter: { id: "starter", name: "Starter Pack", credits: 10, price: 499 },
      pro: { id: "pro", name: "Pro Pack", credits: 25, price: 999 },
      unlimited: {
        id: "unlimited",
        name: "Power Pack",
        credits: 60,
        price: 1999,
      },
    };
  }, []);

  const [trialEligible, setTrialEligible] = useState(true);

  const getSubscriptionPlans = useCallback(async () => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);

      const data = await apiService.getSubscriptionPlans({
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
      // THE ELIGIBILITY ANSWER RIDES ALONG. The server attaches `trial_eligible` to this
      // response precisely so the paywall does not need a second request to find out; it is
      // surfaced as hook state rather than in the return value because two callers already
      // destructure `data.plans` and changing that shape would break the other one.
      // Defaults TRUE when the field is absent (older server, signed-out caller): the offer
      // shows, and `create-subscription` is what actually refuses it.
      setTrialEligible(data.trial_eligible !== false);
      return data.plans;
    } catch (error) {
      console.error("Failed to fetch subscription plans:", error.message);
    }

    // Fallback plans
    return DEFAULT_SUBSCRIPTION_PLANS;
  }, []);

  const createSubscription = async (planId, billingPeriod = "monthly", withdrawalWaiver = false) => {
    if (!userId) throw new Error("You must be signed in to subscribe");

    try {
      const data = await apiService.createSubscription({
        plan_id: planId,
        user_id: userId,
        billing_period: billingPeriod,
        // Reports what the buyer actually ticked. The server decides whether it was required.
        withdrawal_waiver: !!withdrawalWaiver,
        success_url: `${window.location.origin}/credits/success`,
        cancel_url: `${window.location.origin}/credits`,
      });

      if (data.url) {
        window.location.href = data.url;
        return new Promise(() => {}); // Never resolves — page is navigating away
      }
      // A PLAN CHANGE, NOT A SALE. An existing subscriber does not get a hosted checkout
      // page: the server reprices the subscription they already hold, charging the
      // difference on the spot when moving up and queueing the change for their renewal
      // date when moving down. The outcome IS the answer, so it is handed back rather
      // than navigated to.
      if (data.changed) return data;
      throw new Error("No checkout URL received");
    } catch (error) {
      console.error("Error creating subscription:", error);
      throw error;
    }
  };

  const manageSubscription = async () => {
    if (!userId)
      throw new Error("You must be signed in to manage your subscription");

    try {
      const data = await apiService.manageSubscription({
        user_id: userId,
        return_url: `${window.location.origin}/credits`,
      });

      if (data.url) {
        window.location.href = data.url;
        return new Promise(() => {});
      } else {
        throw new Error("No portal URL received");
      }
    } catch (error) {
      console.error("Error managing subscription:", error);
      throw error;
    }
  };

  const getCreditsMessage = () => {
    const remaining = credits.available;
    return `${remaining} video${remaining === 1 ? "" : "s"} remaining this month`;
  };

  const refreshCredits = useCallback(() => {}, []);
  const refreshDailyUsage = useCallback(() => {}, []);

  return {
    credits,
    dailyUsage,
    loading,
    error,
    hasCredits,
    purchaseCredits,
    getCreditPackages,
    createSubscription,
    manageSubscription,
    getSubscriptionPlans,
    trialEligible,
    getCreditsMessage,
    refreshCredits,
    refreshDailyUsage,
    isLowOnCredits: credits.available <= 2,
    isOutOfCredits: credits.available <= 0,
  };
};
